import { cepService } from "@/app/services/cepService";
import { currentStep } from "@/app/store";
import { states } from "@/app/utils/states";
import { zipCodeMask } from "@/app/utils/zipCodeFormater";
import { BasicInput } from "@/view/components/BasicInput";
import { BasicSelect } from "@/view/components/BasicSelect";
import { Button } from "@/view/components/ui/button";
import { useToast } from "@/view/components/ui/use-toast";
import { useAtom } from "jotai";
import { useEffect } from "react";
import { useFormContext } from "react-hook-form";
import { z } from "zod";

interface AddressFormProps {
  visible?: boolean;
}

export const addressFormSchema = z.object({
  zipCode: z
    .string()
    .min(8, "* obrigatório")
    .transform((val) => val.replace(/\D/g, "")),
  neighborhood: z.string().min(4, "* obrigatório"),
  city: z.string().min(4, "* obrigatório"),
  state: z.string({ required_error: "* obrigatório" }).min(2).max(2),
  street: z.string().min(5, "* obrigatório"),
  number: z.string().min(1, "* obrigatório"),
  complement: z.string().optional(),
});

export default function AddressForm({ visible }: AddressFormProps) {
  const [, setStep] = useAtom(currentStep);

  const { toast } = useToast();

  const {
    /*     reset,
    getValues, */
    setValue,
    watch,
    trigger,
    formState: { errors },
  } = useFormContext();

  const handleNext = async () => {
    const isValid = await trigger("address");

    if (isValid) {
      setStep((state) => state + 1);
    }
  };

  const zipCode = watch("address.zipCode");

  useEffect(() => {
    const fetchAddress = async () => {
      const cleanZip = zipCode?.replace(/\D/g, "");
      if (cleanZip?.length === 8) {
        try {
          const address = await cepService(cleanZip);
          if (address && !address.erro) {
            setValue("address.street", address.logradouro);
            setValue("address.neighborhood", address.bairro);
            setValue("address.city", address.localidade);
            setValue("address.state", address.uf);
            setValue("address.complement", address.complemento);
          }
        } catch {
          toast({
            title: "Erro ao buscar CEP",
            description: "Verifique o CEP e tente novamente.",
            variant: "destructive",
          });
        }
      }
    };
    fetchAddress();
  }, [zipCode]);

  return (
    <div className={visible ? "flex flex-col gap-8" : "hidden"}>
      <div className="grid grid-cols-1 gap-4 md:grid-cols-2 lg:grid-cols-4">
        <BasicInput
          name="address.zipCode"
          label="CEP"
          error={(errors.address as any)?.zipCode?.message}
          onInput={zipCodeMask}
          maxLength={9}
        />
      </div>

      <div className="flex gap-4">
        <div className="grow">
          <BasicInput
            name="address.street"
            label="Endereço"
            error={(errors.address as any)?.street?.message}
          />
        </div>
        <div className="w-full max-w-24">
          <BasicInput
            name="address.number"
            label="Número"
            error={(errors.address as any)?.number?.message}
          />
        </div>
      </div>

      <div>
        <div className="grid grid-cols-1 gap-4 md:grid-cols-2 lg:grid-cols-4">
          <BasicInput
            name="address.complement"
            label="Complemento"
            error={(errors.address as any)?.complement?.message}
          />

          <BasicInput
            name="address.neighborhood"
            label="Bairro"
            error={(errors.address as any)?.neighborhood?.message}
          />

          <BasicInput
            name="address.city"
            label="Cidade"
            error={(errors.address as any)?.city?.message}
          />

          <BasicSelect
            label="UF"
            name="address.state"
            options={states}
            placeholder="Selecione o seu estado"
            error={(errors.address as any)?.state?.message}
          />
        </div>
      </div>
      <div className="flex justify-end gap-5">
        <Button
          type="button"
          className="rounded-full bg-white02 px-8 py-3 font-semibold text-primary hover:bg-white02"
          onClick={() => setStep((state) => state - 1)}
        >
          Voltar
        </Button>
        <Button
          className="rounded-full px-8 py-3 font-semibold text-white"
          type="button"
          onClick={handleNext}
        >
          Continuar
        </Button>
      </div>
    </div>
  );
}
